import { ApiError, type OpcionesApi } from '../api/client';
import type { FacturacionCrApi } from './types';

/** Cliente HTTP de Facturación Electrónica Costa Rica (mismo token y mismo formato de error que Panamá). */
export function crearApiCrHttp({ baseUrl, obtenerToken }: OpcionesApi): FacturacionCrApi {
  const raiz = baseUrl.replace(/\/$/, '');
  const base = raiz + '/api/cr/fe';

  async function enviar(metodo: string, url: string, cuerpo?: FormData | object): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = await obtenerToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    let body: BodyInit | undefined;
    if (cuerpo instanceof FormData) body = cuerpo;
    else if (cuerpo !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(cuerpo);
    }
    let resp: Response;
    try {
      resp = await fetch(url, { method: metodo, headers, body });
    } catch {
      throw new ApiError(0, 'No se pudo conectar con el servidor.');
    }
    if (!resp.ok) {
      const texto = await resp.text();
      let json: { titulo?: string; detalles?: unknown } | null = null;
      try {
        json = texto ? JSON.parse(texto) : null;
      } catch {
        json = null;
      }
      if (resp.status === 401) throw new ApiError(401, 'Su sesión expiró. Vuelva a ingresar.');
      if (resp.status === 403 && !json) throw new ApiError(403, 'No tiene permiso para configurar este comercio.');
      throw new ApiError(resp.status, json?.titulo ?? `Error ${resp.status}`, Array.isArray(json?.detalles) ? (json!.detalles as string[]) : []);
    }
    return resp;
  }

  async function req<T>(metodo: string, ruta: string, cuerpo?: FormData | object, absoluta = false): Promise<T> {
    const resp = await enviar(metodo, (absoluta ? raiz : base) + ruta, cuerpo);
    const texto = await resp.text();
    return (texto ? JSON.parse(texto) : null) as T;
  }

  const c = (id: number) => `/comercios/${id}`;
  const d = (id: number, r: { nFactura: number; tipo: string; idReconciliacion: number }) =>
    `${c(id)}/documentos/${r.nFactura}/${r.tipo}/${r.idReconciliacion}`;

  return {
    pais: (id) => req('GET', `/api/fe/comercios/${id}/pais`, undefined, true),
    ubicaciones: () => req('GET', '/catalogos/ubicaciones'),
    obtenerConfiguracion: (id) => req('GET', `${c(id)}/configuracion`),
    guardarEmisor: (id, datos) => req('PUT', `${c(id)}/emisor`, datos),
    guardarCertificado: (id, archivo, pin) => {
      const f = new FormData();
      f.append('Archivo', archivo);
      f.append('Pin', pin);
      return req('PUT', `${c(id)}/certificado`, f);
    },
    guardarCredenciales: (id, usuario, clave) => req('PUT', `${c(id)}/credenciales`, { usuario, clave }),
    productos: (id, estado, buscar) => req('GET', `${c(id)}/productos${qs({ estado, buscar })}`),
    documentos: (id, f) => req('GET', `${c(id)}/documentos${qs({ ...f, pagina: f.pagina ?? 1 })}`),
    documento: (id, r) => req('GET', d(id, r)),
    reemitir: (id, r) => req('POST', `${d(id, r)}/reemitir`),
    reenviarCorreo: (id, r) => req('POST', `${d(id, r)}/reenviar-correo`),
    descargarArchivo: async (id, clave, tipo) => (await enviar('GET', `${base}${c(id)}/archivos/${clave}/${tipo}`)).blob(),
    erroresCarga: (id) => req('GET', `${c(id)}/errores-carga`),
    recargarFactura: (id, n) => req('POST', `${c(id)}/errores-carga/${n}/recargar`),
  };
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const t = p.toString();
  return t ? `?${t}` : '';
}
